import React from 'react';

import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
    CHEAPEST,
    EARLIEST,
    FASTEST,
    LATEST,
    RECOMMENDED,
} from 'wanderu-ducks-search';
import {
    i18nMoment as ourMoment, localize, selectLocale,
} from 'wanderu-i18n';
import { buildSearchUrlPath, changeDay, durationToHoursAndMinutes } from 'wanderu-utils';

import { dayButtonClicked } from './DayButtonEvents';
import { useSortDataSelector, useSortTypeSelector } from '../../../../hooks';

import styles from './DayButton.scss';

import type {
    ActiveSortType,
    DayButtonProps,
    I18nMoney,
    SortData,
} from '../../../../types';
import type { TFunction } from 'i18next';

const adjustReturnDate = ({
    currentReturnDate,
    newDepartDate,
    originalDepartDate,
}: {
    currentReturnDate: string;
    newDepartDate: Date;
    originalDepartDate: string;
}): string => {
    const originalDate = new Date(originalDepartDate);
    // compare the original depart date with the new date the user clicked
    const diff = newDepartDate.getUTCDate() - originalDate.getUTCDate();

    // set returnDate to a date as far in the future as the new date is from the original search depart date
    return changeDay(currentReturnDate, diff);
};

const renderDayOptionData = ({
    activeSortType,
    i18nMoment,
    i18nMoney,
    sortData,
    t,
}: {
    activeSortType: ActiveSortType;
    i18nMoment: typeof ourMoment;
    i18nMoney: I18nMoney;
    sortData: SortData;
    t: TFunction;
}) => {
    const data = sortData?.[activeSortType];
    const cheapest = sortData?.[CHEAPEST];

    if (data === undefined) {
        return undefined;
    }

    if (activeSortType === FASTEST) {
        const duration = durationToHoursAndMinutes(
            i18nMoment.duration(data),
        );

        if (duration.days > 0) {
            return t('baseApp:trip-duration_days', duration);
        }

        return t('baseApp:trip-duration_hours', duration);
    }

    if (cheapest !== undefined && (activeSortType === RECOMMENDED || activeSortType === CHEAPEST)) {
        const price = i18nMoney(cheapest).getTotalPrice();

        return i18nMoney.format(price);
    }

    if (activeSortType === EARLIEST || activeSortType === LATEST) {
        return i18nMoment.parseZone(data).format('LT');
    }

    return undefined;
};

const DayButton = ({
    date,
    i18nMoment,
    i18nMoney,
    isActive,
    isDisabled,
    searchParams,
    t,
}: DayButtonProps) => {
    const activeSortType = useSortTypeSelector(searchParams) || RECOMMENDED;
    const sortData = useSortDataSelector({ date, searchParams });
    const locale = useSelector(selectLocale);
    const history = useHistory();

    const [lang, loc] = locale.split('-');
    const formattedLocale = [lang, loc.toUpperCase()].join('-');

    const formattedDate = new Date(date);
    const dateString = new Intl.DateTimeFormat(formattedLocale, {
        day: 'numeric',
        timeZone: 'UTC',
        weekday: 'short',
    }).format(formattedDate);

    const label = t(`${activeSortType}_tag`);

    const onSelect = () => {
        const queryString = window?.location?.search;
        const queries = Object.fromEntries(new URLSearchParams(queryString).entries());
        let returnDate = searchParams.returnDate;

        if (returnDate && (formattedDate?.getUTCDate() > new Date(returnDate)?.getUTCDate())) {
            // if the return date is in the past, adjust it to be as far in the future as the new date is from the original search depart date
            returnDate = adjustReturnDate({
                currentReturnDate: returnDate,
                newDepartDate: formattedDate,
                originalDepartDate: searchParams.departDate,
            });
        }

        const url = buildSearchUrlPath({
            arrivePlace: searchParams.destinationText,
            arrivePlaceId: searchParams.destinationPlaceId,
            departDate: date,
            departPlace: searchParams.originText,
            departPlaceId: searchParams.originPlaceId,
            returnDate,
        }, queries);

        dayButtonClicked({
            activeSortType,
            date,
            searchParams,
            sortData,
        });

        history.push(`/${locale}${url}`);
    };

    return (
        <button
            className={classNames(
                styles.container,
                {
                    [styles.active]: isActive,
                    [styles.disabled]: isDisabled,
                },
            )}
            disabled={isDisabled}
            onClick={onSelect}
        >
            <span className={styles.date}>
                {dateString}
            </span>

            <span
                aria-label={`${date} ${label}`}
                aria-live="polite"
                className={styles.details}
            >
                {renderDayOptionData({
                    activeSortType,
                    i18nMoment,
                    i18nMoney,
                    sortData,
                    t,
                })}
            </span>
        </button>
    );
};

export default localize(DayButton, [], 'baseApp');
